import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import TopBar from "../sidebar/TopBar";

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
const statsData = [
  { id: 1, label: "Total AI Tools", value: "524", change: "+12", positive: true, icon: "🤖", accent: "#f97316", lightBg: "#fff7ed" },
  { id: 2, label: "Categories", value: "38", change: "+3", positive: true, icon: "📂", accent: "#8b5cf6", lightBg: "#f5f3ff" },
  { id: 3, label: "Total Signups", value: "18240", change: "+8.4%", positive: true, icon: "👥", accent: "#0ea5e9", lightBg: "#f0f9ff" },
  { id: 4, label: "Monthly Visitors", value: "94500", change: "+21.2%", positive: true, icon: "📈", accent: "#10b981", lightBg: "#f0fdf4" },
];

const recentTools = [
  { name: "ChatGPT", company: "OpenAI", category: "Chatbots & Assistants", status: "featured", date: "Today", icon: "💬", color: "#10a37f" },
  { name: "Midjourney", company: "Midjourney Inc.", category: "Image Generation", status: "new", date: "Today", icon: "🎨", color: "#7c3aed" },
  { name: "Claude", company: "Anthropic", category: "Chatbots & Assistants", status: "featured", date: "Yesterday", icon: "🧠", color: "#f97316" },
  { name: "Runway ML", company: "Runway", category: "Video Creation", status: "new", date: "Yesterday", icon: "🎬", color: "#0ea5e9" },
  { name: "Jasper AI", company: "Jasper", category: "Writing & Content", status: "normal", date: "Feb 21", icon: "✍️", color: "#8b5cf6" },
];

const topCategories = [
  { name: "Code & Development", tools: 76, percent: 90, color: "#0ea5e9" },
  { name: "Writing & Content", tools: 68, percent: 80, color: "#8b5cf6" },
  { name: "Image Generation", tools: 52, percent: 62, color: "#ec4899" },
  { name: "Chatbots & Assistants", tools: 45, percent: 54, color: "#f97316" },
  { name: "Productivity", tools: 44, percent: 52, color: "#10b981" },
];

const quickActions = [
  { label: "Add New Tool", icon: "➕", color: "#f97316", bg: "#fff7ed" },
  { label: "Add Category", icon: "📁", color: "#8b5cf6", bg: "#f5f3ff" },
  { label: "Feature a Tool", icon: "⭐", color: "#f59e0b", bg: "#fffbeb" },
  { label: "Manage Users", icon: "👥", color: "#0ea5e9", bg: "#f0f9ff" },
  { label: "Review Submissions", icon: "📋", color: "#10b981", bg: "#f0fdf4" },
  { label: "Site Settings", icon: "⚙️", color: "#6b7280", bg: "#f9fafb" },
];

const pendingSubmissions = [
  { name: "Pika Labs", category: "Video Creation", submittedBy: "user_482", time: "2h ago" },
  { name: "Stable Audio", category: "Audio & Music", submittedBy: "user_291", time: "5h ago" },
  { name: "Perplexity AI", category: "Research", submittedBy: "user_103", time: "1d ago" },
];

// ─── ANIMATED STAT CARD ────────────────────────────────────────────────────────
const StatCard = ({ stat, index }) => {
  const [count, setCount] = useState(0);
  const rawNum = parseFloat(stat.value.replace(/[^0-9.]/g, ""));

  useEffect(() => {
    let start = 0;
    const step = rawNum / 50;
    const timer = setInterval(() => {
      start += step;
      if (start >= rawNum) { setCount(rawNum); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 25);
    return () => clearInterval(timer);
  }, [rawNum]);

  const display = count.toLocaleString("en-IN");

  return (
    <div style={{
      background: "#fff", borderRadius: "16px", padding: "22px",
      border: "1px solid #f1f5f9",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)",
      position: "relative", overflow: "hidden",
      animation: `fadeUp 0.4s ease ${index * 0.08}s both`,
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: stat.accent }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
        <div style={{
          width: "46px", height: "46px", borderRadius: "12px",
          background: stat.lightBg, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: "22px",
        }}>{stat.icon}</div>
        <span style={{
          fontSize: "12px", fontWeight: 700, padding: "4px 10px", borderRadius: "20px",
          background: stat.positive ? "#dcfce7" : "#fee2e2",
          color: stat.positive ? "#16a34a" : "#dc2626",
        }}>{stat.change}</span>
      </div>
      <div style={{ fontSize: "28px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px" }}>{display}</div>
      <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px", fontWeight: 500 }}>{stat.label}</div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { userData } = useSelector((store) => store.userReducer);
  const adminName = userData?.name || userData?.admin_name || "Admin";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .qa-btn:hover { transform: translateY(-3px) !important; box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important; }
        .tool-row:hover { background: #f8fafc !important; }
        .db-wrap { background: #f8fafc; min-height: 100vh; }
      `}</style>

      <div className="db-wrap">
        <TopBar />

        <div style={{ padding: "28px 32px", maxWidth: "1400px", margin: "0 auto" }}>

          {/* ── WELCOME BANNER ── */}
          <div style={{
            background: "#6366f1",
            borderRadius: "20px", padding: "32px 36px", marginBottom: "28px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            position: "relative", overflow: "hidden",
            animation: "fadeUp 0.4s ease both",
          }}>
            <div style={{ position: "absolute", top: "-30px", right: "220px", width: "150px", height: "150px", borderRadius: "50%", background: "rgba(249,115,22,0.1)" }} />
            <div style={{ position: "absolute", bottom: "-50px", right: "60px", width: "200px", height: "200px", borderRadius: "50%", background: "rgba(139,92,246,0.08)" }} />

            <div style={{ position: "relative", zIndex: 1 }}>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "14px", margin: "0 0 6px", fontWeight: 500 }}>{greeting} 👋</p>
              <h1 style={{ color: "#fff", fontSize: "26px", fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.3px" }}>
                Welcome Admin!
              </h1>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", margin: "0 0 16px" }}>{today}</p>
             
            </div>

            <div style={{ display: "flex", gap: "12px", position: "relative", zIndex: 1 }}>
              {[
                { label: "Pending Submissions", value: "12", icon: "📥" },
                { label: "Featured Tools", value: "28", icon: "⭐" },
              ].map((item, i) => (
                <div key={i} style={{
                  background: "rgba(255,255,255,0.07)", backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px",
                  padding: "18px 26px", textAlign: "center", minWidth: "130px",
                }}>
                  <div style={{ fontSize: "20px", marginBottom: "6px" }}>{item.icon}</div>
                  <div style={{ color: "#fff", fontSize: "24px", fontWeight: 800 }}>{item.value}</div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", marginTop: "3px" }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── STAT CARDS ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "18px", marginBottom: "24px" }}>
            {statsData.map((s, i) => <StatCard key={s.id} stat={s} index={i} />)}
          </div>

          {/* ── ROW 2 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>

            {/* Quick Actions */}
            <div style={{
              background: "#fff", borderRadius: "16px", padding: "24px",
              border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              animation: "fadeUp 0.4s ease 0.25s both",
            }}>
              <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: "0 0 18px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ background: "#fff7ed", padding: "5px 8px", borderRadius: "8px" }}>⚡</span> Quick Actions
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px" }}>
                {quickActions.map((a, i) => (
                  <button key={i} className="qa-btn" style={{
                    background: a.bg, border: `1px solid ${a.color}22`, borderRadius: "12px",
                    padding: "16px 8px", cursor: "pointer", textAlign: "center",
                    transition: "all 0.25s ease", display: "flex", flexDirection: "column",
                    alignItems: "center", gap: "8px",
                  }}>
                    <span style={{ fontSize: "22px" }}>{a.icon}</span>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: a.color, lineHeight: 1.3 }}>{a.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pending Submissions */}
            <div style={{
              background: "#fff", borderRadius: "16px", padding: "24px",
              border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              animation: "fadeUp 0.4s ease 0.32s both",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ background: "#fef3c7", padding: "5px 8px", borderRadius: "8px" }}>📥</span> Pending Submissions
                </h3>
                <span style={{ background: "#fee2e2", color: "#dc2626", fontSize: "12px", fontWeight: 700, padding: "3px 10px", borderRadius: "20px" }}>
                  {pendingSubmissions.length} waiting
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {pendingSubmissions.map((sub, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 14px", borderRadius: "10px",
                    background: "#f8fafc", border: "1px solid #e2e8f0",
                  }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>{sub.name}</div>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                        {sub.category} · by {sub.submittedBy} · {sub.time}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={{ background: "#dcfce7", color: "#16a34a", border: "none", borderRadius: "6px", padding: "5px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}>✓ Approve</button>
                      <button style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: "6px", padding: "5px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <button style={{
                width: "100%", marginTop: "14px", padding: "10px", borderRadius: "10px",
                border: "1.5px dashed #e2e8f0", background: "transparent",
                color: "#94a3b8", fontSize: "12px", fontWeight: 600, cursor: "pointer",
              }}>View All Submissions →</button>
            </div>
          </div>

          {/* ── ROW 3 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "20px" }}>

            {/* Recently Added Tools */}
            <div style={{
              background: "#fff", borderRadius: "16px", padding: "24px",
              border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              animation: "fadeUp 0.4s ease 0.38s both",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ background: "#f0f9ff", padding: "5px 8px", borderRadius: "8px" }}>🛠️</span> Recently Added Tools
                </h3>
                <button style={{ fontSize: "12px", color: "#f97316", background: "#fff7ed", border: "none", borderRadius: "8px", padding: "5px 14px", cursor: "pointer", fontWeight: 600 }}>
                  View All
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {recentTools.map((tool, i) => (
                  <div key={i} className="tool-row" style={{
                    display: "flex", alignItems: "center", gap: "14px",
                    padding: "11px 10px", borderRadius: "10px", transition: "background 0.2s",
                  }}>
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "10px",
                      background: tool.color + "22", display: "flex", alignItems: "center",
                      justifyContent: "center", fontSize: "18px", flexShrink: 0,
                    }}>{tool.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>{tool.name}</span>
                        {tool.status === "featured" && (
                          <span style={{ background: "#fef3c7", color: "#d97706", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "4px" }}>FEATURED</span>
                        )}
                        {tool.status === "new" && (
                          <span style={{ background: "#dcfce7", color: "#16a34a", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "4px" }}>NEW</span>
                        )}
                      </div>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>{tool.company} · {tool.category}</div>
                    </div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>{tool.date}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Categories */}
            <div style={{
              background: "#fff", borderRadius: "16px", padding: "24px",
              border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              animation: "fadeUp 0.4s ease 0.45s both",
            }}>
              <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: "0 0 18px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ background: "#f5f3ff", padding: "5px 8px", borderRadius: "8px" }}>📂</span> Top Categories
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {topCategories.map((cat, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>{cat.name}</span>
                      <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 500 }}>{cat.tools} tools</span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: "99px", height: "7px", overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: "99px", background: cat.color,
                        width: `${cat.percent}%`, transition: "width 1.2s ease",
                      }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "22px" }}>
                {[
                  { label: "Avg Tools / Category", value: "13.8", icon: "📊" },
                  { label: "New This Month", value: "+24", icon: "🆕" },
                ].map((item, i) => (
                  <div key={i} style={{ background: "#f8fafc", borderRadius: "10px", padding: "12px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "16px" }}>{item.icon}</div>
                    <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "6px" }}>{item.value}</div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default AdminDashboard;